import { z } from 'zod';

// Zod-схемы для основных Firestore-сущностей. Используем `passthrough()`
// чтобы не падать на новых полях (forward-compat) — Zod возвращает только
// то, что описано в схеме, плюс дополнительные поля как unknown.
//
// Цель — поймать ситуации когда Firestore вернул мусор (миграция, race),
// и логировать их вместо тихого `as Order`/`as MenuItem` где TS не помогает.

const multilingualName = z.object({
  ky: z.string().optional().default(''),
  ru: z.string().optional().default(''),
  en: z.string().optional().default(''),
  zh: z.string().optional().default(''),
});

export const menuItemSchema = z.object({
  id: z.number(),
  names: multilingualName,
  descriptions: multilingualName.optional(),
  price: z.number(),
  image: z.string().optional().default(''),
  inStock: z.boolean().optional(),
  isPopular: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
}).passthrough();

export const orderSchema = z.object({
  orderNumber: z.string().optional(),
  items: z.array(z.any()),
  total: z.number(),
  status: z.enum(['new', 'accepted', 'ready', 'completed', 'cancelled']),
  orderType: z.enum(['dine_in', 'takeaway']),
  paymentMethod: z.enum(['cash', 'card', 'online']),
  isPaid: z.boolean(),
  userId: z.string(),
  // createdAt — Timestamp, в Zod проще обозначить любым объектом
  createdAt: z.any().optional(),
  tableNumber: z.union([z.string(), z.null()]).optional(),
  notes: z.union([z.string(), z.null()]).optional(),
  customerName: z.union([z.string(), z.null()]).optional(),
  customerPhone: z.string().optional(),
  pickupTime: z.any().optional(),
  staffDiscount: z.number().optional(),
  staffName: z.string().optional(),
  staffPin: z.string().optional(),
  originalTotal: z.number().optional(),
  promoCode: z.string().optional(),
  promoType: z.enum(['percent', 'fixed']).optional(),
  promoValue: z.number().optional(),
}).passthrough();

export const serviceRequestSchema = z.object({
  type: z.enum(['waiter', 'bill', 'napkins', 'menu', 'clean', 'other']),
  tableNumber: z.string(),
  status: z.enum(['new', 'completed']),
  userId: z.string(),
  createdAt: z.any().optional(),
}).passthrough();

/**
 * Best-effort парсер: если Zod не проходит — логируем (раз на тип ошибки),
 * но возвращаем `data` as T, чтобы не ломать рендер. Цель — surface bugs,
 * не падать прод. В вызывающем коде можно использовать guarded fields.
 */
const warned = new Set<string>();
export function parseFirestoreDoc<T>(
  schemaName: string,
  data: unknown,
  schema: z.ZodTypeAny
): T {
  const result = schema.safeParse(data);
  if (!result.success && !warned.has(schemaName)) {
    warned.add(schemaName);
    console.warn(`[firestore] ${schemaName} schema mismatch:`, result.error.issues.slice(0, 3));
  }
  return (result.success ? result.data : data) as T;
}
